import React, { Suspense, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useScroll, useMotionValueEvent } from "framer-motion";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { ProductShowcaseCard } from "@/components/ProductShowcaseCard";
import { usePrefillArea } from "@/hooks/use-contact-prefill";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const SceneC = React.lazy(() => import("@/components/scenes/SceneC"));
const SceneH = React.lazy(() => import("@/components/scenes/SceneH"));

function useMotionValueState(mv, initial = 0) {
  const [value, setValue] = useState(initial);
  useMotionValueEvent(mv, "change", (v) => setValue(v));
  return value;
}

function SceneFallback() {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        background: "var(--surface-base)",
      }}
    />
  );
}

function Hero() {
  const { t } = useT("platformMarketing");
  const locale = useLocale();
  return (
    <section
      className="container-frac pt-24 pb-16 md:pt-32 md:pb-24"
      data-testid="marketing-hero"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow" data-testid="page-eyebrow">
            {t("hero.eyebrow")}
          </span>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("hero.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="marketing-cta-primary"
            >
              {t("hero.cta")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Strategization() {
  const { t } = useT("platformMarketing");
  const strategic = t("strategization.strategic", { returnObjects: true }) || {};
  const executive = t("strategization.executive", { returnObjects: true }) || {};
  return (
    <section
      className="container-frac py-16 md:py-24"
      data-testid="marketing-strategization"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("strategization.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("strategization.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p
            className="mt-8 text-lg leading-normal text-ink-secondary"
            style={{ maxWidth: "62ch" }}
          >
            {t("strategization.body")}
          </p>
        </Reveal>
      </div>

      <div
        className="mt-16 grid grid-cols-1 lg:grid-cols-2"
        style={{ columnGap: "48px", rowGap: "64px" }}
      >
        <Reveal>
          <ProductShowcaseCard
            testId="marketing-showcase-strategic"
            label={strategic.label}
            name={strategic.name}
            tagline={strategic.tagline}
            description={strategic.description}
            primary={{
              src: "/products/suprai-strategic/01-hero.jpg",
              alt: "SuprAI Strategic Advisor — decision output with readiness score and weighted assessment bars",
            }}
            secondaries={[
              { src: "/products/suprai-strategic/02-framework.jpg", alt: "SuprAI Strategic Advisor — six-stage assessment framework" },
              { src: "/products/suprai-strategic/03-assessment.jpg", alt: "SuprAI Strategic Advisor — executive intent wizard" },
            ]}
          />
        </Reveal>

        <Reveal delay={120}>
          <ProductShowcaseCard
            testId="marketing-showcase-executive"
            label={executive.label}
            name={executive.name}
            tagline={executive.tagline}
            description={executive.description}
            primary={{
              src: "/products/suprai-executive/01-dashboard.jpg",
              alt: "SuprAI Executive Command Centre — portfolio dashboard",
            }}
            secondaries={[
              { src: "/products/suprai-executive/02-portfolio.jpg", alt: "SuprAI Executive Command Centre — initiative portfolio table" },
              { src: "/products/suprai-executive/03-decisions.jpg", alt: "SuprAI Executive Command Centre — decision room" },
            ]}
          />
        </Reveal>
      </div>
    </section>
  );
}

function ComingSoon() {
  const { t } = useT("platformMarketing");
  return (
    <section
      className="container-frac py-14 md:py-20"
      data-testid="marketing-coming-soon"
    >
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-12">
        <div className="max-w-xl">
          <Reveal>
            <span
              className="eyebrow inline-flex items-center gap-2"
              data-testid="marketing-coming-soon-eyebrow"
            >
              <span
                aria-hidden="true"
                className="animate-pulse"
                style={{
                  display: "inline-block",
                  width: "6px",
                  height: "6px",
                  borderRadius: "var(--radius-full)",
                  background: "var(--frac-violet)",
                }}
              />
              {t("comingSoon.eyebrow")}
            </span>
          </Reveal>
          <Reveal delay={120}>
            <h3
              className="mt-4 font-bold text-ink-primary"
              style={{
                fontSize: "25px",
                fontWeight: 700,
                lineHeight: "var(--leading-tight)",
                letterSpacing: "var(--tracking-tight)",
              }}
              data-testid="marketing-coming-soon-h3"
            >
              {t("comingSoon.h3")}
            </h3>
          </Reveal>
          <Reveal delay={240}>
            <p
              className="mt-4 max-w-prose text-base leading-normal text-ink-secondary"
              style={{ fontWeight: 400 }}
            >
              {t("comingSoon.body")}
            </p>
          </Reveal>
        </div>

        <div
          className="relative w-full"
          style={{ height: "380px" }}
          data-testid="marketing-coming-soon-scene"
        >
          <Suspense fallback={<SceneFallback />}>
            <SceneH style={{ position: "absolute", inset: 0 }} />
          </Suspense>
          <noscript>
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "var(--radius-lg)",
                background:
                  "radial-gradient(120% 120% at 75% 75%, #7200DB 0%, #2A0A4A 55%, #02040B 100%)",
              }}
            />
          </noscript>
        </div>
      </div>

      <style>{`
        @media (min-width: 768px) {
          [data-testid="marketing-coming-soon-scene"] { height: 420px; }
        }
        @media (min-width: 1024px) {
          [data-testid="marketing-coming-soon-scene"] { height: 460px; }
        }
      `}</style>
    </section>
  );
}

function WhatItDoes() {
  const { t } = useT("platformMarketing");
  const capabilities = t("whatItDoes.capabilities", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-16 md:py-24"
      data-testid="marketing-what"
    >
      <Reveal>
        <span className="eyebrow">{t("whatItDoes.eyebrow")}</span>
      </Reveal>
      <MaskReveal
        as="h2"
        className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
      >
        {t("whatItDoes.h2")}
      </MaskReveal>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" gap={70}>
        {capabilities.map((c, i) => (
          <StaggerItem key={i}>
            <Card variant="glass" className="h-full" data-testid={`marketing-cap-${i}`}>
              <h3 className="text-base font-bold text-ink-primary">{c.name}</h3>
              <p className="mt-3 text-sm leading-normal text-ink-secondary">
                {c.body}
              </p>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function PrivacyMembrane() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useMotionValueState(scrollYProgress, 0);
  const { t } = useT("platformMarketing");

  return (
    <section
      ref={ref}
      className="relative"
      data-testid="marketing-privacy"
    >
      <div className="relative grid grid-cols-1 md:grid-cols-2 md:min-h-[200vh]">
        <div className="md:sticky md:top-0 flex items-center py-16 md:h-screen">
          <div className="px-6 md:pl-16 md:pr-8">
            <Reveal>
              <span className="eyebrow">{t("privacy.eyebrow")}</span>
            </Reveal>
            <MaskReveal
              as="h2"
              className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
            >
              {t("privacy.h2")}
            </MaskReveal>
            <Reveal delay={200}>
              <p className="mt-8 text-lg leading-normal text-ink-secondary">
                {t("privacy.body")}
              </p>
            </Reveal>
          </div>
        </div>
        <div className="md:sticky md:top-0 md:h-screen">
          <div
            className="relative h-[60vh] w-full overflow-hidden border-l border-hairline md:h-full"
            data-testid="marketing-scene-c-wrapper"
          >
            <Suspense fallback={<SceneFallback />}>
              <SceneC
                scrollProgress={progress}
                style={{ position: "absolute", inset: 0 }}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
}

function InYourTenant() {
  const { t } = useT("platformMarketing");
  const chips = t("tenant.chips", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="marketing-tenant"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("tenant.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("tenant.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("tenant.body")}
          </p>
        </Reveal>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="marketing-tenant-chips">
          {chips.map((chip, i) => (
            <Badge key={i} variant="violet" onDark>{chip}</Badge>
          ))}
        </div>
      </div>
    </section>
  );
}

function Onboarding() {
  const { t } = useT("platformMarketing");
  const steps = t("onboarding.steps", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="marketing-onboarding"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("onboarding.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl"
        >
          {t("onboarding.h2")}
        </MaskReveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" gap={70}>
        {steps.map((s) => (
          <StaggerItem key={s.number}>
            <Card variant="glass" className="h-full" data-testid={`marketing-step-${s.number}`}>
              <div
                className="text-4xl font-light leading-none text-frac-violet"
                style={{ fontWeight: 300 }}
              >
                {s.number}
              </div>
              <h3 className="mt-3 text-lg font-bold text-ink-primary">{s.name}</h3>
              <p className="mt-3 text-sm leading-normal text-ink-secondary">
                {s.body}
              </p>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function FinalCta() {
  const { t } = useT("platformMarketing");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="marketing-final-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <MaskReveal
          as="h2"
          className="text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("finalCta.h2")}
        </MaskReveal>
        <Reveal delay={300}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="marketing-final-cta-primary"
            >
              {t("finalCta.cta")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function PlatformMarketing() {
  usePrefillArea("marketing_technology");

  return (
    <>
      <PageSeo
        title="Marketing Technology — SuprAI Marketing Terminal"
        description="AI-driven campaign, content and lead workflows deployed inside your own tenant. Marketing that runs itself. Your data never leaves your walls."
        path="/platform/marketing"
        image="/og/platform-marketing.png"
      />
      <div data-testid="page-platform-marketing">
        <Hero />
        <Strategization />
        <ComingSoon />
        <WhatItDoes />
        <PrivacyMembrane />
        <InYourTenant />
        <Onboarding />
        <FinalCta />
      </div>
    </>
  );
}
